/**
 * Generic deep snake_case ⇄ camelCase converters for API payloads.
 *
 * These handle the common 1:1 case mapping for nested objects and arrays so the
 * per-domain mappers stay thin. Acronym-heavy fields that don't round-trip
 * cleanly (e.g. `assignedCAName`) are fixed up explicitly in the domain mapper
 * AFTER the generic pass.
 */

type Json = unknown

function snakeKey(key: string): string {
  return key.replace(/([A-Z])/g, '_$1').replace(/__/g, '_').toLowerCase()
}

function camelKey(key: string): string {
  return key.replace(/_([a-z0-9])/g, (_, c: string) => c.toUpperCase())
}

function isPlainObject(value: Json): value is Record<string, Json> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function toCamelDeep<T = unknown>(input: Json): T {
  if (Array.isArray(input)) {
    return input.map((v) => toCamelDeep(v)) as unknown as T
  }
  if (isPlainObject(input)) {
    const out: Record<string, Json> = {}
    for (const [k, v] of Object.entries(input)) {
      out[camelKey(k)] = toCamelDeep(v)
    }
    return out as T
  }
  return input as T
}

export function toSnakeDeep<T = unknown>(input: Json): T {
  if (Array.isArray(input)) {
    return input.map((v) => toSnakeDeep(v)) as unknown as T
  }
  if (isPlainObject(input)) {
    const out: Record<string, Json> = {}
    for (const [k, v] of Object.entries(input)) {
      out[snakeKey(k)] = toSnakeDeep(v)
    }
    return out as T
  }
  return input as T
}
